import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import SectionHeader from './SectionHeader';
import PhoneNumberInput from './PhoneNumberInput';
import TeamMemberEmailField from './TeamMemberEmailField';
import { formatPhoneForDisplay } from '../utils/phoneNumber';
import { teamMemberEmailPayload } from '../utils/teamMemberEmail';
import './TransactionTeam.css';
import { getListingsApiUrl } from '../utils/apiUrls';
import {
  LISTING_ADD_PARTY_ROLES,
  canonicalListingRoleValue,
  isListingSellerPrincipal,
  listingRoleLabel,
  listingTeamSortIndex,
} from '../utils/listingTeamRoles';

const LISTINGS_URL = getListingsApiUrl();
const ROLES = LISTING_ADD_PARTY_ROLES;
const getRoleLabel = listingRoleLabel;
const isSeller = isListingSellerPrincipal;
const getTeamSortIndex = listingTeamSortIndex;

const ListingTeam = ({ listingId, userEmail, isExpanded, onToggle }) => {
  const { user } = useAuth0();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState({ role: 'seller', name: '', email: '', phone: '', company: '' });

  useEffect(() => {
    if (listingId && isExpanded) fetchTeam();
  }, [listingId, isExpanded]);

  const fetchTeam = async () => {
    const email = userEmail || user?.email;
    try {
      setLoading(true);
      const res = await axios.get(`${LISTINGS_URL}/${listingId}/team`, { params: { user_email: email } });
      setMembers(res.data);
    } catch (err) {
      setError('Failed to load team members.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const openAddModal = () => {
    setEditingMember(null);
    setFormData({ role: 'seller', name: '', email: '', phone: '', company: '' });
    setShowModal(true);
  };

  const openEditModal = (member) => {
    setEditingMember(member);
    setFormData({
      role: canonicalListingRoleValue(member.role),
      name: member.name || '',
      email: member.email || '',
      phone: member.phone || '',
      company: member.company || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const email = userEmail || user?.email;
    try {
      setLoading(true);
      if (editingMember) {
        await axios.put(`${LISTINGS_URL}/${listingId}/parties/${editingMember.id}`, {
          user_email: email,
          role: formData.role,
          name: formData.name,
          ...teamMemberEmailPayload(editingMember, formData.email),
          phone: formData.phone,
          company: formData.company,
        });
      } else {
        await axios.post(`${LISTINGS_URL}/${listingId}/parties`, {
          ...formData,
          user_email: email,
        });
      }
      await fetchTeam();
      setShowModal(false);
    } catch (err) {
      alert('Failed to save: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!editingMember || !window.confirm('Remove this party?')) return;
    const email = userEmail || user?.email;
    try {
      setLoading(true);
      await axios.delete(`${LISTINGS_URL}/${listingId}/parties/${editingMember.id}`, {
        params: { user_email: email },
      });
      await fetchTeam();
      setShowModal(false);
      setEditingMember(null);
    } catch (err) {
      alert('Failed to remove: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  if (!listingId) return null;

  const sellers = members.filter(m => isSeller(m.role));
  const others = members
    .filter(m => !isSeller(m.role))
    .sort((a, b) => getTeamSortIndex(a.role) - getTeamSortIndex(b.role) || (a.id ?? 0) - (b.id ?? 0));

  const renderCard = (member, variant = 'standard') => {
    const isMinimal = variant === 'minimal';
    return (
      <div key={member.id} className={`team-member-card ${isMinimal ? 'card-minimal' : 'card-branded'}`}>
        {!isMinimal && (
          <div className="card-custom-header">
            <span className="header-role">{getRoleLabel(member.role)}</span>
            <button className="edit-icon-btn header-edit" onClick={(e) => { e.stopPropagation(); openEditModal(member); }}>✎</button>
          </div>
        )}
        <div className="member-info">
          <div className="member-name-row">
            <div className="member-name-group">
              <span className="member-name">{member.name || 'Unknown Name'}</span>
              {member.is_entity && <span className="member-company">Entity</span>}
              {!member.is_entity && member.signs_for_entity_name && (
                <span className="member-company">{`Signing for ${member.signs_for_entity_name}`}</span>
              )}
              {member.company && !member.is_entity && !member.signs_for_entity_name && (
                <span className="member-company">{member.company}</span>
              )}
            </div>
            {isMinimal && (
              <button className="edit-icon-btn" onClick={(e) => { e.stopPropagation(); openEditModal(member); }}>✎</button>
            )}
          </div>
          <span className="member-email">{member.email || 'No Email'}</span>
          {member.phone && <span className="member-email">{formatPhoneForDisplay(member.phone)}</span>}
        </div>
      </div>
    );
  };

  return (
    <div className="transaction-team-container">
      <SectionHeader title="Key People" isExpanded={isExpanded} onToggle={onToggle}>
        <div className="section-controls">
          <button
            className="add-member-header-btn"
            onClick={(e) => { e.stopPropagation(); openAddModal(); }}
            title="Add Party"
          >
            + Add Party
          </button>
        </div>
      </SectionHeader>

      {isExpanded && (
        <div className="team-content">
          {loading && <div className="loading">Loading...</div>}
          {error && <div className="error">{error}</div>}

          <div className="key-people-grid" style={{ gridTemplateColumns: '1fr 2fr' }}>
            <div className="people-column column-seller">
              <div className="column-card">
                <h3 className="column-header column-header-navy">{sellers.length > 1 ? 'Sellers' : 'Seller'}</h3>
                <div className="column-content">
                  {sellers.map(m => renderCard(m, 'minimal'))}
                </div>
              </div>
            </div>

            <div className="people-column column-others">
              <div className="column-card">
                <h3 className="column-header column-header-green">Listing Team</h3>
                <div className="column-content grid-others">
                  {others.map(m => renderCard(m, 'standard'))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingMember ? 'Edit Party' : 'Add Party'}</h3>
              <button className="close-modal-btn" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Role <span style={{ color: 'red', marginLeft: '4px' }}>*</span></label>
                <select name="role" value={formData.role} onChange={handleInputChange}
                  disabled={!!editingMember && editingMember.role?.toLowerCase() !== 'participant'}
                  className={!!editingMember && editingMember.role?.toLowerCase() !== 'participant' ? 'disabled-input' : ''}>
                  {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Name <span style={{ color: 'red', marginLeft: '4px' }}>*</span></label>
                <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="Full name" />
              </div>
              <div className="form-group">
                <label>Company / Brokerage</label>
                <input type="text" name="company" value={formData.company} onChange={handleInputChange} placeholder="Company name" />
              </div>
              <div className="form-group">
                <label>Email</label>
                <TeamMemberEmailField
                  member={editingMember}
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <PhoneNumberInput name="phone" value={formData.phone} onChange={handleInputChange} placeholder="e.g. 405-555-1234" />
              </div>
              <div className="form-actions" style={{ justifyContent: 'space-between' }}>
                <div>
                  {editingMember && (
                    <button type="button" className="btn-danger" onClick={handleDelete}>Remove Party</button>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                  <button type="submit" className="btn-primary" disabled={loading}>
                    {loading ? 'Saving...' : (editingMember ? 'Save Changes' : 'Add Party')}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListingTeam;
